import { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

export type ConfirmVariant = 'default' | 'danger'

export interface ConfirmModalProps {
  isOpen: boolean
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: ConfirmVariant
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}

export function ConfirmModal({
  isOpen,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
  onConfirm,
  onClose,
}: Readonly<ConfirmModalProps>) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  const handleClose = useCallback(() => {
    dialogRef.current?.close()
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) dialogRef.current?.showModal()
  }, [isOpen])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !isOpen) return

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        handleClose()
      }
    }

    dialog.addEventListener('mousedown', handleBackdropClick)
    return () => dialog.removeEventListener('mousedown', handleBackdropClick)
  }, [isOpen, handleClose])

  if (!isOpen) return null

  const confirmClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 focus:ring-blue-500'

  if (typeof document === 'undefined') return null

  return createPortal(
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      className='fixed top-1/2 left-1/2 z-[100] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden [&::backdrop]:bg-black/40 [&::backdrop]:backdrop-blur-[2px]'
      aria-labelledby='confirm-modal-title'
    >
      <div className='px-6 py-5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100'>
        <h3
          id='confirm-modal-title'
          className='text-base font-semibold text-slate-900'
        >
          {title}
        </h3>
        {description && (
          <p className='mt-1 text-sm text-slate-600 leading-relaxed'>
            {description}
          </p>
        )}
      </div>

      <div className='px-6 py-5 flex gap-3 justify-end'>
        <button
          type='button'
          onClick={handleClose}
          disabled={loading}
          className='px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-60 transition-colors'
        >
          {cancelText}
        </button>
        <button
          type='button'
          onClick={onConfirm}
          disabled={loading}
          className={`px-4 py-2.5 rounded-xl text-white font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 transition-all ${confirmClass}`}
        >
          {loading ? 'Please wait…' : confirmText}
        </button>
      </div>
    </dialog>,
    document.body
  )
}

export default ConfirmModal
